/**
 * src/components/public/forms/IletisimFormu.tsx
 *
 * Iletisim formu — React Hook Form + Zod + Cloudflare Turnstile + /api/iletisim
 * POST handler.
 *
 * Client Component — RHF runtime + Turnstile script (window callbacks) icin
 * 'use client' gerekli.
 *
 * Form fields:
 *   - Ad Soyad (text, required, min 2)
 *   - E-posta (email, required)
 *   - Telefon (text, opsiyonel)
 *   - Konu (select: Sizma Testi / ISO Danismanlik / SOC / Diger, required)
 *   - Mesaj (textarea, required, min 20 char)
 *   - KVKK consent checkbox (required = true)
 *   - Cloudflare Turnstile widget (env'de NEXT_PUBLIC_TURNSTILE_SITE_KEY varsa render)
 *   - Submit button
 *
 * Submit akisi:
 *   1. Browser-side Zod validate (RHF)
 *   2. Turnstile token retrieval (window.turnstile.getResponse)
 *   3. POST /api/iletisim (turnstileToken + form data)
 *   4. Success/error toast
 *
 * Turnstile hata yonetimi:
 *   Widget error/expired/timeout callback'leri ile beslenen `turnstileError`
 *   state'i, dogrulama tamamlanamadiginda kullaniciya sebebi + "tekrar dene"
 *   butonu + telefon/e-posta kacis yolu gosterir. Aksi halde kisi takili bir
 *   kutuya bakar, submit'te anlamsiz bir hata alir ve genelde vazgecer —
 *   sitenin tek donusum noktasinda kayip demektir (Sentry TurnstileError
 *   300010, 2026-08-01).
 *
 * Field shell'leri ./iletisim-form-fields.tsx icine extract edildi (300-line
 * component limiti icin).
 */
'use client'

import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import Script from 'next/script'
import { useRef, useState } from 'react'

import {
  ILETISIM_KONU_OPTIONS,
  iletisimFormSchema,
  type IletisimFormValues,
  type IletisimKonu,
} from '@/lib/forms/iletisim-schema'
import { Link } from '@/lib/i18n/navigation'
import { trackEvent } from '@/lib/analytics/gtag'

import {
  ERROR_CLASS,
  INPUT_CLASS,
  INPUT_ERROR_CLASS,
  LABEL_CLASS,
  TextAreaField,
  TextField,
} from './iletisim-form-fields'

/**
 * Tip yardimcisi — RHF field hata mesajini t() key'i olarak alip cevirir.
 *
 * @param messageKey Zod schema'dan gelen i18n key (orn. 'forms.iletisim.validation.email_invalid')
 * @param t Translation fonksiyonu (next-intl)
 * @returns Locale-aware hata mesaji veya bos string
 */
function translateError(
  messageKey: string | undefined,
  t: (key: string) => string,
): string {
  if (!messageKey) return ''
  // Zod schema'da 'forms.iletisim.validation.X' prefix'i ile geliyor; t fonksiyonu
  // 'forms.iletisim' namespace'inden cagrildigi icin son segment'leri cikar.
  const segments = messageKey.split('.')
  if (segments.length >= 3 && segments[0] === 'forms' && segments[1] === 'iletisim') {
    return t(segments.slice(2).join('.'))
  }
  return messageKey
}

/**
 * IletisimFormu — KVKK uyumlu kurumsal iletisim formu (Phase α non-functional).
 *
 * Phase α'da onSubmit handler placeholder ('mesaj geldi' alert); Phase α Week 5
 * task'inda /api/iletisim route handler'ina POST yapilacak (Turnstile token +
 * server-side Zod parse + rate-limit + pg insert).
 */
/**
 * Turnstile site key (public, build-time embedding). Yoksa widget render
 * edilmez ve form Turnstile-free calismaya devam eder (dev mode).
 */
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? ''

declare global {
  interface Window {
    turnstile?: {
      render: (selector: string | HTMLElement, opts: Record<string, unknown>) => string
      reset: (widgetId?: string) => void
      getResponse: (widgetId?: string) => string | undefined
    }
  }
}

type SubmitStatus =
  | { kind: 'idle' }
  | { kind: 'success' }
  | { kind: 'error'; message: string }

export function IletisimFormu() {
  // forms.iletisim namespace, konu_options + kvkk i18n key'leri buradan.
  // Onceki `typeof document !== 'undefined'` patterni hydration mismatch
  // (React #418) atiyordu: server false, client true → text diff. Cozum:
  // tum locale-aware text'leri t() ile cevirmek (next-intl context, SSR uyumlu).
  const t = useTranslations('forms.iletisim')

  const [status, setStatus] = useState<SubmitStatus>({ kind: 'idle' })
  /**
   * Turnstile widget'inin son hata kodu. null = sorun yok.
   *
   * Neden gerekli: Turnstile varsayilan olarak `retry: auto` ile sessizce
   * yeniden dener; kullanici ekranda takili bir kutu gorur ama HICBIR aciklama
   * almaz. Sonra 'Gonder'e basinca 'robot dogrulamasi gerekli' yazisiyla
   * karsilasir — hic calisan bir dogrulama gormemis birine anlamsiz gelir.
   * Bu state, hatayi gorunur kilip kullaniciya kacis yolu (tekrar dene +
   * dogrudan iletisim) sunmak icin tutulur.
   */
  const [turnstileError, setTurnstileError] = useState<string | null>(null)
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null)
  const turnstileWidgetIdRef = useRef<string | null>(null)

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<IletisimFormValues>({
    resolver: zodResolver(iletisimFormSchema),
    defaultValues: {
      adSoyad: '',
      email: '',
      telefon: '',
      mesaj: '',
      // RHF default'larda 'false' yaziyoruz; Zod literal(true) kontrolu submit'te
      // checkbox checked olunca z.literal(true) gecer. Type cast cunku type
      // IletisimFormValues 'true' bekliyor.
      kvkkConsent: false as unknown as true,
    },
    mode: 'onBlur',
  })

  /**
   * Turnstile script onLoad — site key varsa widget render et.
   *
   * Callback sozlesmesi (Cloudflare "Widget configurations" dokumani):
   *   - callback          : token alindi → varsa eski hatayi temizle
   *   - error-callback    : ag hatasi VEYA challenge basarisiz (300* ailesi)
   *   - expired-callback  : token 300 saniye sonra dustu, widget reset EDILMEZ
   *   - timeout-callback  : interaktif challenge suresinde cozulmedi
   *
   * `retry` varsayilani `auto` (8sn arayla) — bilerek degistirilmiyor; amac
   * yeniden denemeyi durdurmak degil, denenirken kullaniciyi bilgilendirmek.
   * error-callback'ten deger DONMUYORUZ: `return true` semantigi Cloudflare
   * dokumaninda tanimli degil, ezbere davranisa yaslanmayiz.
   */
  function handleTurnstileScriptLoad() {
    if (!TURNSTILE_SITE_KEY || !turnstileContainerRef.current) return
    if (turnstileWidgetIdRef.current) return
    // Bazi engelleyiciler istegi reddetmek yerine BOS 200 ile karsilar: onLoad
    // calisir ama window.turnstile tanimsiz kalir. Sessizce donersek kullanici
    // hic widget gormez ve nedenini bilmez — hatayi gorunur kil.
    if (!window.turnstile) {
      setTurnstileError('script-unavailable')
      return
    }
    turnstileWidgetIdRef.current = window.turnstile.render(
      turnstileContainerRef.current,
      {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'auto',
        // Otomatik retry basarili olursa eski hata mesaji ekranda kalmasin.
        callback: () => setTurnstileError(null),
        'error-callback': (code?: string) => setTurnstileError(code ?? 'unknown'),
        'expired-callback': () => setTurnstileError('expired'),
        'timeout-callback': () => setTurnstileError('timeout'),
      },
    )
  }

  /**
   * Kullanici "Tekrar dene"ye bastiginda widget'i sifirdan calistirir.
   * Gecici ag/eklenti kaynakli 300* hatalarinda cogu zaman yeterli olur.
   */
  function handleTurnstileRetry() {
    setTurnstileError(null)
    if (window.turnstile && turnstileWidgetIdRef.current) {
      window.turnstile.reset(turnstileWidgetIdRef.current)
      return
    }
    // Script hic yuklenmediyse reset edilecek bir widget yok; tek anlamli
    // aksiyon sayfayi yenilemek (kullanici bu arada engelleyiciyi kapatmis
    // olabilir).
    window.location.reload()
  }

  /**
   * onSubmit — POST /api/iletisim. Turnstile token + form data ile.
   *
   * Hata durumlari:
   *   - TURNSTILE_MISSING / FAILED: kullaniciya 'robot dogrulamasi gerekli'
   *   - VALIDATION_FAILED: ilgili alana mesaj (server-side double-check)
   *   - RATE_LIMIT: 'cok fazla istek, X saniye sonra'
   *   - DB_ERROR / 5xx: 'simdi kaydedilemedi, tekrar deneyin'
   */
  const onSubmit = handleSubmit(async (values) => {
    setStatus({ kind: 'idle' })

    let turnstileToken = ''
    if (TURNSTILE_SITE_KEY && window.turnstile && turnstileWidgetIdRef.current) {
      turnstileToken = window.turnstile.getResponse(turnstileWidgetIdRef.current) ?? ''
      if (!turnstileToken) {
        setStatus({
          kind: 'error',
          message: t('validation.turnstile_required'),
        })
        return
      }
    } else if (TURNSTILE_SITE_KEY) {
      // Site key var ama widget hazir degil
      setStatus({
        kind: 'error',
        message: t('validation.turnstile_required'),
      })
      return
    }

    try {
      const res = await fetch('/api/iletisim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, turnstileToken }),
      })
      if (res.ok) {
        setStatus({ kind: 'success' })
        // Analitik: başarılı form gönderimi (konu boyutuyla — onay varsa GA'ya).
        trackEvent('form_submit', { form: 'iletisim', konu: values.konu })
        reset()
        if (window.turnstile && turnstileWidgetIdRef.current) {
          window.turnstile.reset(turnstileWidgetIdRef.current)
        }
      } else {
        const errBody = (await res.json().catch(() => null)) as
          | { error?: { code?: string; message?: string } }
          | null
        trackEvent('form_error', {
          form: 'iletisim',
          code: errBody?.error?.code ?? `http_${res.status}`,
        })
        setStatus({
          kind: 'error',
          message: errBody?.error?.message ?? t('error_body'),
        })
      }
    } catch {
      trackEvent('form_error', { form: 'iletisim', code: 'network' })
      setStatus({ kind: 'error', message: t('error_body') })
    }
  })

  return (
    <form onSubmit={onSubmit} noValidate aria-label={t('title')} className="space-y-5">
      <TextField
        id="iletisim-adSoyad"
        label={t('ad_soyad_label')}
        autoComplete="name"
        required
        placeholder={t('ad_soyad_placeholder')}
        error={errors.adSoyad}
        errorText={translateError(errors.adSoyad?.message, t)}
        register={register('adSoyad')}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <TextField
          id="iletisim-email"
          label={t('email_label')}
          type="email"
          autoComplete="email"
          required
          placeholder={t('email_placeholder')}
          error={errors.email}
          errorText={translateError(errors.email?.message, t)}
          register={register('email')}
        />
        <TextField
          id="iletisim-telefon"
          label={t('telefon_label')}
          type="tel"
          autoComplete="tel"
          placeholder={t('telefon_placeholder')}
          error={errors.telefon}
          errorText={translateError(errors.telefon?.message, t)}
          register={register('telefon')}
        />
      </div>

      {/* Konu (select) — Controller cunku native select RHF register ile ok ama
          tip narrowing icin Controller daha temiz. */}
      <div>
        <label htmlFor="iletisim-konu" className={LABEL_CLASS}>
          {t('konu_label')}{' '}
          <span aria-hidden="true" className="text-red-500">
            *
          </span>
        </label>
        <Controller
          control={control}
          name="konu"
          render={({ field }) => (
            <select
              id="iletisim-konu"
              required
              aria-required="true"
              aria-invalid={Boolean(errors.konu)}
              aria-describedby={errors.konu ? 'iletisim-konu-error' : undefined}
              className={`${INPUT_CLASS} ${errors.konu ? INPUT_ERROR_CLASS : ''}`}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value as IletisimKonu)}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
            >
              <option value="" disabled>
                {t('konu_placeholder')}
              </option>
              {ILETISIM_KONU_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {t(`konu_options.${opt}`)}
                </option>
              ))}
            </select>
          )}
        />
        {errors.konu && (
          <p id="iletisim-konu-error" role="alert" aria-live="polite" className={ERROR_CLASS}>
            {translateError(errors.konu.message, t)}
          </p>
        )}
      </div>

      <TextAreaField
        id="iletisim-mesaj"
        label={t('mesaj_label')}
        rows={6}
        required
        placeholder={t('mesaj_placeholder')}
        error={errors.mesaj}
        errorText={translateError(errors.mesaj?.message, t)}
        register={register('mesaj')}
      />

      {/* KVKK consent */}
      <div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            required
            aria-required="true"
            aria-invalid={Boolean(errors.kvkkConsent)}
            aria-describedby={errors.kvkkConsent ? 'iletisim-kvkk-error' : undefined}
            className="mt-0.5 size-4 rounded border-slate-300 dark:border-slate-700 text-brand-600 focus:ring-brand-500 focus:ring-offset-0"
            {...register('kvkkConsent')}
          />
          <span className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            <Link
              href="/kvkk-aydinlatma"
              className="text-brand-700 dark:text-brand-300 underline hover:no-underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('kvkk_link_label')}
            </Link>
            {t('kvkk_consent_suffix')}{' '}
            <span aria-hidden="true" className="text-red-500">
              *
            </span>
          </span>
        </label>
        {errors.kvkkConsent && (
          <p id="iletisim-kvkk-error" role="alert" aria-live="polite" className={ERROR_CLASS}>
            {translateError(errors.kvkkConsent.message, t)}
          </p>
        )}
      </div>

      {/* Turnstile widget container — site key varsa render edilir */}
      {TURNSTILE_SITE_KEY && (
        <div>
          <div ref={turnstileContainerRef} className="cf-turnstile" />

          {/* Dogrulama yuklenemediginde: sebep + tekrar dene + kacis yolu.
              Kirmizi DEGIL amber — bu bir form hatasi degil, gecici bir engel;
              kullanicinin girdiginde yanlis bir sey yok. data-turnstile-error
              destek/debug icin ham kodu (orn. 300010) DOM'da tutar. */}
          {turnstileError && (
            <div
              role="alert"
              aria-live="polite"
              data-turnstile-error={turnstileError}
              className="mt-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-100"
            >
              <strong className="block font-semibold mb-1">{t('turnstile_error_title')}</strong>
              <span className="block">{t('turnstile_error_body')}</span>
              <button
                type="button"
                onClick={handleTurnstileRetry}
                className="mt-3 inline-flex items-center justify-center px-4 py-2 rounded-md border border-amber-400 dark:border-amber-700 bg-white dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 font-medium hover:bg-amber-100 dark:hover:bg-amber-900/70 transition-colors"
              >
                {t('turnstile_retry')}
              </button>
            </div>
          )}
        </div>
      )}

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          aria-disabled={isSubmitting}
          className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-700 hover:bg-brand-800 dark:bg-brand-600 dark:hover:bg-brand-500 text-white font-semibold rounded-lg transition-colors shadow-sm shadow-brand-700/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? t('submitting') : t('submit')}
        </button>
      </div>

      {/* Status messages — basari / hata toast'lari */}
      {status.kind === 'success' && (
        <div
          role="status"
          aria-live="polite"
          className="mt-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-900 dark:text-emerald-100"
        >
          <strong className="block font-semibold mb-1">{t('success_title')}</strong>
          <span>{t('success_body')}</span>
        </div>
      )}
      {status.kind === 'error' && (
        <div
          role="alert"
          aria-live="polite"
          className="mt-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-900 dark:text-red-100"
        >
          <strong className="block font-semibold mb-1">{t('error_title')}</strong>
          <span>{status.message}</span>
        </div>
      )}

      {/* Turnstile script — site key varsa async yukle */}
      {TURNSTILE_SITE_KEY && (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js"
          strategy="afterInteractive"
          onLoad={handleTurnstileScriptLoad}
          // Reklam/gizlilik engelleyicilerinin EN yaygin davranisi script
          // istegini tamamen kesmektir; o durumda onLoad hic calismaz ve
          // widget callback'leri kaydolmaz. onError bu yolu yakalar.
          // (next/script: Client Component + afterInteractive ile destekli)
          onError={() => setTurnstileError('script-blocked')}
        />
      )}
    </form>
  )
}
